class CutoffTime extends HTMLElement {
    timer = 0

    constructor() {
        super()

        this.text = this.querySelector('[js-cutoff-time="text"]')
        this.cutoff = this.getAttribute("data-cutoff")
        this.leadHours = Number(this.getAttribute("data-lead-hours"))
        this.countryIsoCode = this.getAttribute("data-country-iso-code")
        this.bankHolidays = this.getAttribute("data-bank-holidays")
    }

    connectedCallback() {
        if (!this.cutoff || !this.leadHours || !this.text) {
            throw new Error("Missing required attributes")
        }

        this.startCountdown()
    }

    disconnectedCallback() {
        if (this.timer) {
            window.clearInterval(this.timer)
        }
    }

    isDateBankHoliday(date, timeZone) {
        // If no bank holidays are set, it can't be a bank holiday
        if (!this.bankHolidays) return false

        const holidays = JSON.parse(this.bankHolidays)
        const dateStr = date.toLocaleDateString("en-CA", { timeZone }) // YYYY-MM-DD format

        return holidays.includes(dateStr)
    }

    getNextValidDeliveryDate(startDate, timeZone) {
        // Convert leadHours to working days (assuming 8-hour workday)
        const workingDays = Math.ceil(this.leadHours / 24)
        const deliveryDate = new Date(startDate)

        // Add working days one by one, skipping weekends and holidays
        let daysAdded = 0
        while (daysAdded < workingDays) {
            deliveryDate.setDate(deliveryDate.getDate() + 1)

            // Skip if weekend or holiday
            if (
                deliveryDate.getDay() !== 0 && // not Sunday
                deliveryDate.getDay() !== 6 && // not Saturday
                !this.isDateBankHoliday(deliveryDate, timeZone)
            ) {
                daysAdded++
            }
        }

        return deliveryDate
    }

    startCountdown() {
        const updateCountdown = () => {
            const isUnitedKingdom = this.countryIsoCode === "GB"
            const timeZone = isUnitedKingdom ? "Europe/London" : "Europe/Paris"
            const timeZoneOptions = { timeZone }

            const now = new Date()
            const [hours, minutes] = this.cutoff.split(":").map(Number)

            const target = new Date(now)
            target.setHours(hours, minutes, 0, 0)

            if (now > target) {
                target.setDate(target.getDate() + 1)
            }

            const deliveryDate = this.getNextValidDeliveryDate(target, timeZone)

            // Calculate the cutoff time by going backward from delivery date
            const cutoffTime = new Date(target)

            // Calculate time difference
            const diff = cutoffTime.getTime() - now.getTime()

            // Handle case where time might be negative
            if (diff < 0) {
                // Recalculate for next day if time is negative
                target.setDate(target.getDate() + 1)
                const newDeliveryDate = this.getNextValidDeliveryDate(target, timeZone)
                const newCutoffTime = new Date(target)
                const newDiff = newCutoffTime.getTime() - now.getTime()

                this.updateCountdownDisplay(newDiff, newDeliveryDate, timeZoneOptions)
            } else {
                this.updateCountdownDisplay(diff, deliveryDate, timeZoneOptions)
            }
        }

        updateCountdown()
        // Set interval just once here, don't reset it on every update
        this.timer = window.setInterval(updateCountdown, 60_000)
    }

    updateCountdownDisplay(diff, deliveryDate, timeZoneOptions) {
        const daysLeft = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hoursLeft = Math.floor(
            (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        )
        const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

        const weekday = deliveryDate.toLocaleString("default", {
            weekday: "long",
            ...timeZoneOptions
        })

        const month = deliveryDate.toLocaleString("default", {
            month: "long",
            ...timeZoneOptions
        })

        const day = deliveryDate.toLocaleString("default", {
            day: "numeric",
            ...timeZoneOptions
        })

        const ordinal = d => {
            if (d > 3 && d < 21) return `${d}th`
            switch (d % 10) {
                case 1:
                    return `${d}st`
                case 2:
                    return `${d}nd`
                case 3:
                    return `${d}rd`
                default:
                    return `${d}th`
            }
        }

        const originalText = this.text.getAttribute("data-text")
        const countdownText =
            daysLeft > 0
                ? `${daysLeft}d ${hoursLeft
                    .toString()
                    .padStart(2, "0")}h ${minutesLeft.toString().padStart(2, "0")}m`
                : `${hoursLeft
                    .toString()
                    .padStart(2, "0")}h ${minutesLeft.toString().padStart(2, "0")}m`

        const formattedDate = `${weekday} ${ordinal(parseInt(day))} ${month}`

        this.text.innerHTML = originalText
            .replace("##time##", `<strong>${countdownText}</strong>`)
            .replace("##date##", `<strong>${formattedDate}</strong>`)

        setTimeout(() => {
            const property = document.querySelector(
                '[js-product-form="delivery-date"]'
            )

            if (property) {
                property.setAttribute(
                    "value",
                    deliveryDate.toLocaleDateString("en-GB", {
                        weekday: "long",
                        day: "numeric",
                        month: "long"
                    })
                )
            }
        }, 100)
    }
}

customElements.define("uwp-cutoff-time", CutoffTime)
